import { SEO } from '../components/common/SEO';
import { Layout } from '../components/common/Layout';

const steps = [
    {
        number: '1',
        title: 'Open Swag Shots App',
        description: 'Launch the Swag Shots application on your mobile device (iOS or Android).',
        icon: '📱',
    },
    {
        number: '2',
        title: 'Go to Profile Screen',
        description: 'Tap on the Profile icon at the bottom of the screen to open your profile page.',
        icon: '👤',
    },
    {
        number: '3',
        title: 'Open Settings',
        description: 'Look at the top right corner of your profile screen and tap the Settings icon (⚙️).',
        icon: '⚙️',
    },
    {
        number: '4',
        title: 'Scroll to Bottom',
        description: 'In the Settings menu, scroll all the way down to the bottom where you will see various options.',
        icon: '�',
    },
    {
        number: '5',
        title: 'Tap Delete Account',
        description: 'At the bottom of the settings list, you will find the "Delete Account" button. Tap on it to proceed.',
        icon: '🗑️',
    },
    {
        number: '6',
        title: 'Confirm Deletion',
        description: 'Read the warning message carefully, then tap "Confirm" to permanently delete your account and all data.',
        icon: '✓',
    },
];

const importantNotes = [
    {
        title: 'Permanent Action',
        description: 'Account deletion is permanent and cannot be undone. All your data will be permanently removed from our servers.',
    },
    {
        title: 'Data Removed',
        description: 'Your profile, videos, comments, likes, followers, following, messages, and all other personal data will be permanently deleted.',
    },
    {
        title: 'Username Release',
        description: 'Your username will become available for others to use after account deletion.',
    },
    {
        title: 'Processing Time',
        description: 'Account deletion may take up to 30 days to complete. During this time, your account will be deactivated and hidden from other users.',
    },
];

export const AccountDeletionPage: React.FC = () => {
    return (
        <Layout>
            <SEO
                title="Account Deletion Guide - Swag Shots"
                description="Learn how to delete your Swag Shots account. Step-by-step instructions for permanently removing your account and all associated data."
                url="https://www.swagshots.com/account-deletion"
            />

            {/* Hero Section */}
            <section className="bg-gradient-to-br from-red-600 to-red-500 text-white pt-24 pb-16">
                <div className="container-custom">
                    <div className="text-center max-w-3xl mx-auto">
                        <h1 className="text-4xl md:text-5xl font-bold mb-6">Account Deletion</h1>
                        <p className="text-xl text-red-100">
                            Step-by-step guide to permanently delete your Swag Shots account
                        </p>
                    </div>
                </div>
            </section>

            {/* Main Content */}
            <div className="container-custom py-16">
                <div className="max-w-4xl mx-auto">
                    {/* Introduction */}
                    <section className="mb-12">
                        <div className="bg-blue-50 border-l-4 border-blue-500 p-6 rounded-r-lg">
                            <h2 className="text-xl font-semibold text-blue-900 mb-2">
                                Account Deletion Available In-App
                            </h2>
                            <p className="text-blue-800 mb-3">
                                Account deletion is available directly within the Swag Shots app. Follow the simple steps below:
                            </p>
                            <div className="bg-white rounded-lg p-4 border border-blue-200">
                                <p className="text-blue-900 font-medium">
                                    📱 Profile → ⚙️ Settings (top right) → Scroll to bottom → Delete Account → Confirm
                                </p>
                            </div>
                        </div>
                    </section>

                    {/* Step-by-Step Guide */}
                    <section className="mb-16">
                        <h2 className="text-3xl font-bold text-gray-900 mb-4">Step-by-Step Deletion Guide</h2>
                        <p className="text-gray-600 mb-8">
                            Follow these simple steps to safely delete your Swag Shots account. Please read each step carefully.
                        </p>
                        <div className="space-y-6">
                            {steps.map((step, index) => (
                                <div
                                    key={index}
                                    className="bg-white rounded-xl shadow-md border border-gray-200 p-6 hover:shadow-lg transition-shadow"
                                >
                                    <div className="flex items-start space-x-4">
                                        <div className="flex-shrink-0">
                                            <div className="w-12 h-12 bg-primary-600 text-white rounded-full flex items-center justify-center text-xl font-bold">
                                                {step.number}
                                            </div>
                                        </div>
                                        <div className="flex-grow">
                                            <div className="flex items-center space-x-2 mb-2">
                                                <span className="text-2xl">{step.icon}</span>
                                                <h3 className="text-xl font-semibold text-gray-900">{step.title}</h3>
                                            </div>
                                            <p className="text-gray-700">{step.description}</p>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Visual Flow Diagram */}
                        <div className="mt-8 bg-gradient-to-r from-primary-50 to-blue-50 rounded-xl p-6 border-2 border-primary-200">
                            <h3 className="text-lg font-semibold text-gray-900 mb-4 text-center">Quick Navigation Path</h3>
                            <div className="flex flex-wrap items-center justify-center gap-2 text-center">
                                <div className="bg-white px-4 py-2 rounded-lg shadow-sm border border-primary-200">
                                    <span className="text-2xl">📱</span>
                                    <p className="text-sm font-medium text-gray-900 mt-1">Open App</p>
                                </div>
                                <span className="text-primary-600 font-bold text-xl">→</span>
                                <div className="bg-white px-4 py-2 rounded-lg shadow-sm border border-primary-200">
                                    <span className="text-2xl">👤</span>
                                    <p className="text-sm font-medium text-gray-900 mt-1">Profile</p>
                                </div>
                                <span className="text-primary-600 font-bold text-xl">→</span>
                                <div className="bg-white px-4 py-2 rounded-lg shadow-sm border border-primary-200">
                                    <span className="text-2xl">⚙️</span>
                                    <p className="text-sm font-medium text-gray-900 mt-1">Settings</p>
                                    <p className="text-xs text-gray-500">(top right)</p>
                                </div>
                                <span className="text-primary-600 font-bold text-xl">→</span>
                                <div className="bg-white px-4 py-2 rounded-lg shadow-sm border border-primary-200">
                                    <span className="text-2xl">📜</span>
                                    <p className="text-sm font-medium text-gray-900 mt-1">Scroll Down</p>
                                </div>
                                <span className="text-primary-600 font-bold text-xl">→</span>
                                <div className="bg-white px-4 py-2 rounded-lg shadow-sm border border-primary-200">
                                    <span className="text-2xl">🗑️</span>
                                    <p className="text-sm font-medium text-gray-900 mt-1">Delete Account</p>
                                </div>
                                <span className="text-primary-600 font-bold text-xl">→</span>
                                <div className="bg-white px-4 py-2 rounded-lg shadow-sm border border-primary-200">
                                    <span className="text-2xl">✓</span>
                                    <p className="text-sm font-medium text-gray-900 mt-1">Confirm</p>
                                </div>
                            </div>
                        </div>
                    </section>

                    {/* Important Information */}
                    <section className="mb-16">
                        <h2 className="text-3xl font-bold text-gray-900 mb-8">⚠️ Important Safety Information</h2>

                        {/* Critical Warning */}
                        <div className="bg-gradient-to-r from-red-50 to-orange-50 border-2 border-red-400 rounded-xl p-6 mb-8">
                            <div className="flex items-start space-x-3">
                                <span className="text-4xl">🛑</span>
                                <div>
                                    <h3 className="text-xl font-bold text-red-900 mb-3">
                                        Please Read Carefully Before Proceeding
                                    </h3>
                                    <p className="text-red-800 mb-3 font-medium">
                                        Account deletion is a <strong>permanent and irreversible action</strong>. Once you confirm deletion,
                                        there is no way to recover your account, videos, followers, or any other data.
                                    </p>
                                    <ul className="space-y-2 text-red-800">
                                        <li className="flex items-start">
                                            <span className="mr-2">❌</span>
                                            <span>You <strong>CANNOT</strong> undo this action</span>
                                        </li>
                                        <li className="flex items-start">
                                            <span className="mr-2">❌</span>
                                            <span>All your content will be <strong>permanently lost</strong></span>
                                        </li>
                                        <li className="flex items-start">
                                            <span className="mr-2">❌</span>
                                            <span>Your username will be <strong>released for others</strong> to use</span>
                                        </li>
                                        <li className="flex items-start">
                                            <span className="mr-2">❌</span>
                                            <span>Customer support <strong>cannot restore</strong> deleted accounts</span>
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </div>

                        {/* What Gets Deleted */}
                        <div className="bg-yellow-50 border-2 border-yellow-300 rounded-xl p-6 mb-8">
                            <div className="flex items-start space-x-3">
                                <span className="text-3xl">📋</span>
                                <div className="flex-grow">
                                    <h3 className="text-lg font-semibold text-yellow-900 mb-3">
                                        What Will Be Permanently Deleted
                                    </h3>
                                    <div className="grid md:grid-cols-2 gap-3">
                                        <div className="flex items-start space-x-2">
                                            <span className="text-yellow-700">•</span>
                                            <span className="text-yellow-800">All your videos and posts</span>
                                        </div>
                                        <div className="flex items-start space-x-2">
                                            <span className="text-yellow-700">•</span>
                                            <span className="text-yellow-800">Profile information and photos</span>
                                        </div>
                                        <div className="flex items-start space-x-2">
                                            <span className="text-yellow-700">•</span>
                                            <span className="text-yellow-800">Comments and likes</span>
                                        </div>
                                        <div className="flex items-start space-x-2">
                                            <span className="text-yellow-700">•</span>
                                            <span className="text-yellow-800">Followers and following lists</span>
                                        </div>
                                        <div className="flex items-start space-x-2">
                                            <span className="text-yellow-700">•</span>
                                            <span className="text-yellow-800">Direct messages and chats</span>
                                        </div>
                                        <div className="flex items-start space-x-2">
                                            <span className="text-yellow-700">•</span>
                                            <span className="text-yellow-800">Watch history and preferences</span>
                                        </div>
                                        <div className="flex items-start space-x-2">
                                            <span className="text-yellow-700">•</span>
                                            <span className="text-yellow-800">Virtual coins and rewards</span>
                                        </div>
                                        <div className="flex items-start space-x-2">
                                            <span className="text-yellow-700">•</span>
                                            <span className="text-yellow-800">All account settings</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Detailed Notes */}
                        <div className="grid md:grid-cols-2 gap-6">
                            {importantNotes.map((note, index) => (
                                <div
                                    key={index}
                                    className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm"
                                >
                                    <h3 className="font-semibold text-gray-900 mb-2 flex items-center">
                                        <span className="text-red-500 mr-2">•</span>
                                        {note.title}
                                    </h3>
                                    <p className="text-gray-700 text-sm">{note.description}</p>
                                </div>
                            ))}
                        </div>
                    </section>

                    {/* Alternative Options */}
                    <section className="mb-16">
                        <h2 className="text-3xl font-bold text-gray-900 mb-6">🤔 Not Sure? Consider These Alternatives</h2>
                        <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-xl p-6 border-2 border-green-300">
                            <p className="text-gray-800 mb-6 font-medium">
                                If you're concerned about privacy, taking a break, or just need some space, you might want to
                                consider these safer alternatives instead of permanently deleting your account:
                            </p>
                            <div className="space-y-4">
                                <div className="bg-white rounded-lg p-5 border border-green-200 shadow-sm">
                                    <div className="flex items-start space-x-3">
                                        <span className="text-2xl">🔒</span>
                                        <div>
                                            <h3 className="font-bold text-gray-900 mb-1">Make Your Account Private</h3>
                                            <p className="text-gray-700 text-sm">
                                                Control who can see your content and follow you. Go to Settings → Privacy → Private Account.
                                            </p>
                                        </div>
                                    </div>
                                </div>
                                <div className="bg-white rounded-lg p-5 border border-green-200 shadow-sm">
                                    <div className="flex items-start space-x-3">
                                        <span className="text-2xl">⏸️</span>
                                        <div>
                                            <h3 className="font-bold text-gray-900 mb-1">Temporarily Deactivate Your Account</h3>
                                            <p className="text-gray-700 text-sm">
                                                Take a break without losing your data. Your account will be hidden and you can reactivate
                                                anytime. Available in Settings → Account → Deactivate Account.
                                            </p>
                                        </div>
                                    </div>
                                </div>
                                <div className="bg-white rounded-lg p-5 border border-green-200 shadow-sm">
                                    <div className="flex items-start space-x-3">
                                        <span className="text-2xl">📥</span>
                                        <div>
                                            <h3 className="font-bold text-gray-900 mb-1">Download Your Data First</h3>
                                            <p className="text-gray-700 text-sm">
                                                Save a copy of all your videos and data before deleting. Go to Settings → Privacy →
                                                Download My Data.
                                            </p>
                                        </div>
                                    </div>
                                </div>
                                <div className="bg-white rounded-lg p-5 border border-green-200 shadow-sm">
                                    <div className="flex items-start space-x-3">
                                        <span className="text-2xl">🔕</span>
                                        <div>
                                            <h3 className="font-bold text-gray-900 mb-1">Adjust Notification Settings</h3>
                                            <p className="text-gray-700 text-sm">
                                                Reduce distractions by turning off push notifications. Go to Settings → Notifications.
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>

                    {/* Support Section */}
                    <section className="mb-16">
                        <h2 className="text-3xl font-bold text-gray-900 mb-6">Need Help?</h2>
                        <div className="bg-white border-2 border-primary-200 rounded-xl p-6">
                            <p className="text-gray-700 mb-4">
                                If you're having trouble deleting your account or have questions about the process, our
                                support team is here to help.
                            </p>
                            <div className="flex flex-col sm:flex-row gap-4">
                                <a
                                    href="/contact"
                                    className="inline-flex items-center justify-center px-6 py-3 bg-primary-600 text-white font-medium rounded-lg hover:bg-primary-700 transition-colors"
                                >
                                    Contact Support
                                </a>
                                <a
                                    href="/privacy-policy"
                                    className="inline-flex items-center justify-center px-6 py-3 bg-gray-100 text-gray-700 font-medium rounded-lg hover:bg-gray-200 transition-colors"
                                >
                                    Privacy Policy
                                </a>
                            </div>
                        </div>
                    </section>

                    {/* Legal Notice */}
                    <section className="text-sm text-gray-600 border-t border-gray-200 pt-8">
                        <p className="mb-2">
                            <strong>Legal Notice:</strong> By deleting your account, you acknowledge that:
                        </p>
                        <ul className="list-disc list-inside space-y-1 ml-4">
                            <li>All your personal data, content, and account information will be permanently deleted</li>
                            <li>This action is irreversible and you will not be able to recover your account or data</li>
                            <li>Any remaining account balance or rewards will be forfeited</li>
                            <li>You understand and accept the terms outlined in our Privacy Policy and Terms of Service</li>
                        </ul>
                        <p className="mt-4 text-xs">
                            Last updated: October 20, 2025
                        </p>
                    </section>
                </div>
            </div>
        </Layout>
    );
};
